/**
 * PostHog analytics — COPPA-safe.
 * monitoringSKILL Rules 1–6.
 *
 * This is the ONLY file allowed to call posthog.capture().
 * Import Analytics.* everywhere else — NEVER call posthog directly.
 *
 * COPPA invariants (enforced by code review + CI lint rule):
 *   - No question text, answer text, or hint content in any event
 *   - No student names, emails, or any PII
 *   - persistence = "memory" — never writes to localStorage or cookies (D-012)
 *   - session recording permanently disabled (D-013)
 *   - COPPA child exclusion: safeCapture() is a no-op when role === 'student'
 *     Children's behavioral data must NEVER reach PostHog or any third-party
 *     analytics service. Call identifyUser() after profile loads; the guard
 *     automatically suppresses all events until then if role is student.
 */
import posthog from 'posthog-js'

// ── COPPA child exclusion ─────────────────────────────────────────────────────
// Set by identifyUser(); cleared by resetAnalyticsUser() on sign-out.
// null = not yet identified (captures allowed — e.g. anonymous landing events).
let _currentRole: 'student' | 'parent' | null = null

/**
 * Drop-in replacement for posthog.capture() with COPPA child exclusion.
 * Students' behavioral data must never reach PostHog (COPPA §312.2 "operator").
 *
 * Exported for use by infrastructure-level reporters (e.g. vitals.ts) that are
 * mounted in ALL sessions including students. NEVER call posthog.capture() directly.
 */
export function safeCapture(event: string, properties?: Record<string, unknown>): void {
  if (_currentRole === 'student') return  // COPPA: children's data must never reach PostHog
  posthog.capture(event, properties)
}

// ── Initialization ────────────────────────────────────────────────────────────

export function initPostHog(): void {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: 'https://app.posthog.com',

    // D-012 — memory persistence: never writes to localStorage or cookies (COPPA)
    persistence: 'memory',

    // Disable all automatic capture (Rule 5 / D-013)
    autocapture:               false,
    capture_pageview:          false,
    capture_pageleave:         false,
    disable_session_recording: true,

    // Disable surveys feature — we never use it and it loads an extra 24 KB
    // surveys.js from the PostHog CDN on every page load
    disable_surveys: true,

    // Strip URL query params that could accidentally contain PII tokens
    sanitize_properties(properties) {
      const raw = properties['$current_url']
      if (typeof raw === 'string') {
        try {
          const url = new URL(raw)
          url.search = ''
          properties['$current_url'] = url.toString()
        } catch { /* ignore invalid URLs */ }
      }
      return properties
    },
  })
}

// ── Identity ──────────────────────────────────────────────────────────────────

/**
 * Rule 2 — only opaque userId + role. NO name, email, grade, or PII.
 * COPPA: students are never identified in PostHog — call is silently suppressed.
 * Call this once after profile loads in AuthContext.
 */
export function identifyUser(userId: string, role: 'student' | 'parent'): void {
  _currentRole = role
  if (role === 'student') return  // COPPA: do not identify children in PostHog
  posthog.identify(userId, { role })
}

export function resetAnalyticsUser(): void {
  _currentRole = null
  posthog.reset()
}

// ── Event catalog ─────────────────────────────────────────────────────────────
// Rule 6 — ALL posthog.capture() calls MUST live in this object.
// Rule 1 — property values NEVER contain question/answer/hint text.
// All calls use safeCapture() — automatically NOOP for student role (COPPA).

export const Analytics = {

  // ── Conversion funnel ─────────────────────────────────────────
  // These are the 5 named events that define the parent conversion funnel.
  // Critical events (trial_converted, subscription_created) ALSO fire
  // server-side from stripe-webhook Edge Function for ad-blocker resilience.

  /** Fired when a parent selects their role on the signup screen. */
  signupStarted: (role: 'parent' | 'student') =>
    safeCapture('signup_started', { role }),

  /** Fired when a parent completes onboarding (profile + child account saved). */
  onboardingCompleted: (role: string, gradeLevel: number) =>
    safeCapture('onboarding_completed', { role, grade_level: gradeLevel }),

  /** Fired when a parent creates a child account via create-child-account. */
  childAccountCreated: () =>
    safeCapture('child_account_created'),

  /**
   * Fired when a K-12 checkout.session.completed event arrives in stripe-webhook.
   * Also fired server-side directly from the Edge Function for ad-blocker resilience.
   * Client-side call here covers the Stripe redirect-back UX.
   */
  trialConverted: (plan: 'monthly' | 'annual') =>
    safeCapture('trial_converted', { plan }),

  // ── Onboarding (legacy — kept for dashboard compatibility) ────
  onboardingStarted: () =>
    safeCapture('onboarding_started'),

  onboardingSkipped: (step: number) =>
    safeCapture('onboarding_skipped', { step }),

  // ── Sessions ──────────────────────────────────────────────────
  sessionStarted: (
    subject: string, topic: string,
    mode: 'practice' | 'quiz', gradeLevel: number
  ) =>
    safeCapture('session_started', { subject, topic, mode, grade_level: gradeLevel }),

  sessionCompleted: (opts: {
    subject:      string
    mode:         'practice' | 'quiz'
    duration:     number    // seconds
    accuracy:     number    // 0.0–1.0
    hintsUsed:    number    // aggregate count only — not content
    gradeLevel:   number
    usedFallback: boolean   // true if offline fallback questions were served
  }) =>
    safeCapture('session_completed', {
      subject:          opts.subject,
      mode:             opts.mode,
      duration_seconds: Math.round(opts.duration),
      accuracy_pct:     Math.round(opts.accuracy * 100),
      hints_used:       opts.hintsUsed,
      grade_level:      opts.gradeLevel,
      used_fallback:    opts.usedFallback,
    }),

  sessionAbandoned: (subject: string, mode: string, questionIndex: number) =>
    safeCapture('session_abandoned', { subject, mode, question_index: questionIndex }),

  // ── Hints ─────────────────────────────────────────────────────
  // Only log subject + hint level — NEVER the hint content (Rule 1)
  hintUsed: (subject: string, hintLevel: 1 | 2 | 3) =>
    safeCapture('hint_used', { subject, hint_level: hintLevel }),

  // ── Medals + achievements ──────────────────────────────────────
  medalAwarded: (subject: string, tier: 'bronze' | 'silver' | 'gold', scorePct: number) =>
    safeCapture('medal_awarded', { subject, medal_tier: tier, score_pct: scorePct }),

  streakMilestone: (days: number) =>
    safeCapture('streak_milestone', { streak_days: days }),

  // ── Subscription ──────────────────────────────────────────────
  subscriptionStarted: (plan: 'monthly' | 'annual') =>
    safeCapture('subscription_started', { plan }),

  subscriptionCancelled: (plan: 'monthly' | 'annual', daysActive: number) =>
    safeCapture('subscription_cancelled', { plan, days_active: daysActive }),

  trialStarted: () =>
    safeCapture('trial_started'),

  trialExpired: () =>
    safeCapture('trial_expired'),

  // ── Navigation ────────────────────────────────────────────────
  pageViewed: (page: string) =>
    safeCapture('page_viewed', { page }),

  // ── Landing page (marketing) ──────────────────────────────────
  // Rule 1 — no PII: only the CTA identifier and an optional plan label.
  landingPageViewed: () =>
    safeCapture('landing_page_viewed'),

  landingCtaClicked: (cta: string, plan?: 'monthly' | 'annual' | 'schools') =>
    safeCapture('landing_cta_clicked', plan ? { cta, plan } : { cta }),

  // ── PWA ───────────────────────────────────────────────────────
  pwaInstalled: (platform: 'android' | 'ios' | 'desktop') =>
    safeCapture('pwa_installed', { platform }),

  offlineSessionStarted: (subject: string) =>
    safeCapture('offline_session_started', { subject }),

  // ── Explanation card ──────────────────────────────────────────
  explanationRevealed: (opts: { wasCorrect: boolean; gradeLevel: number; stepCount: number; isFallback: boolean }) =>
    safeCapture('explanation_revealed', {
      was_correct: opts.wasCorrect,
      grade_level: opts.gradeLevel,
      step_count:  opts.stepCount,
      is_fallback: opts.isFallback,
    }),

  explanationSkipped: (opts: { wasCorrect: boolean; gradeLevel: number; stepsShown: number; isFallback: boolean }) =>
    safeCapture('explanation_skipped', {
      was_correct:  opts.wasCorrect,
      grade_level:  opts.gradeLevel,
      steps_shown:  opts.stepsShown,
      is_fallback:  opts.isFallback,
    }),

  // ── Errors (product-level — separate from Sentry) ─────────────
  geminiApiFallback: (subject: string, grade: number) =>
    safeCapture('gemini_api_fallback', { subject, grade_level: grade }),

  // ── AI provider routing (circuit breaker) ────────────────────
  // No question text or PII — only which provider served the question.
  // Used to monitor Claude fallback rate for cost tracking.
  questionProvider: (provider: 'gemini' | 'claude' | 'fallback') =>
    safeCapture('question_provider', { provider }),

} as const

// ── BANNED — never add ────────────────────────────────────────────────────────
// posthog.capture('question_answered', { question: '...' })  ← Rule 1 VIOLATION
// posthog.capture('hint_shown',        { content:  '...' })  ← Rule 1 VIOLATION
// posthog.capture('signed_up',         { email:    '...' })  ← Rule 2 VIOLATION
// posthog.capture(anything)            without safeCapture() ← COPPA VIOLATION
